import axios, { AxiosInstance, AxiosRequestHeaders } from 'axios';

// import setTokenWithExpiry from 'utils/setTokenWithExpiry';
import breakpoints from './breakpoints';

const { REACT_APP_BLOX_BI_URL } = process.env;

class BloxBiService {
  instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: REACT_APP_BLOX_BI_URL,
      maxRedirects: 0, // Prevent automatic redirects
    });
    this.instance.interceptors.request.use(async (config) => {
      const token = localStorage.getItem('BLOX_USER_TOKEN');

      (config.headers as AxiosRequestHeaders).authorization = `Bearer ${token}` as string;
      (config.headers as AxiosRequestHeaders)['Content-Type'] = 'application/json';
      config.timeout = 60 * 4 * 1000;
      return config;
    });

    // this.instance.interceptors.response.use(
    //   (response) => {
    //     if (response.headers.token !== 'null') {
    //       setTokenWithExpiry(response.headers.token);
    //     } else if (localStorage.getItem('BLOX_USER_TOKEN') === 'null') {
    //       localStorage.removeItem('BLOX_USER_TOKEN');
    //     }
    //     return response;
    //   },
    //   (error) => {
    //     if (
    //       error.response.data.message === 'Token is invalid !!' &&
    //       !window.location.pathname.includes('create')
    //     ) {
    //       const isSignUpPage = window.location.pathname === '/signup';

    //       localStorage.removeItem('BLOX_USER_TOKEN');
    //       localStorage.removeItem('BLOX_USER_TOKEN_EXPIRY');

    //       window.location.href = !isSignUpPage ? '/' : '/signup';
    //     }
    //     return Promise.reject(error);
    //   },
    // );
  }

  // bi services
  createDashboardChart = ({ request_body }: any) => {
    return this.instance.post(breakpoints.createchart(), request_body);
  };

  updateDashboardChart = ({ request_body }: any) => {
    return this.instance.put(breakpoints.createchart(), request_body);
  };

  getDashboardChart = (chartId: any) => {
    return this.instance.get(breakpoints.getChart(chartId));
  };

  getDashboardGraph = (chartId: any, scenarioId: number, requestBody: any) =>
    this.instance.post(breakpoints.getGraph(chartId, scenarioId), requestBody);

  addChartMetric = ({ request_body }: any) =>
    this.instance.post(breakpoints.addMetric(), request_body);

  updateChartMetric = ({ request_body }: any) =>
    this.instance.put(breakpoints.addMetric(), request_body);

  addChartFilter = ({ request_body }: any) =>
    this.instance.post(breakpoints.chartFilter(), request_body);

  updateConditionalFormatting = ({ request_body }: any) =>
    this.instance.put(breakpoints.conditionalFormatting(), request_body);

  addConditionalFormatting = ({ request_body }: any) =>
    this.instance.post(breakpoints.conditionalFormatting(), request_body);

  getAllCharts = (dashboardId: string) => this.instance.get(breakpoints.allCharts(dashboardId));

  updateChartLayout = ({ request_body }: any) =>
    this.instance.put(breakpoints.updateLayout(), request_body);

  updateInstrumentType = ({ request_body }: any) =>
    this.instance.put(breakpoints.updateInstrumentType(), request_body);

  updateChartFilter = ({ request_body }: any) =>
    this.instance.put(breakpoints.chartFilter(), request_body);

  deleteChartFilter = (filterId: string) =>
    this.instance.delete(breakpoints.deleteFilter(filterId));

  deleteConditionalFormatting = (conditionId: string) =>
    this.instance.delete(breakpoints.deleteConditional(conditionId));

  deleteChart = (chartId: any) => this.instance.delete(breakpoints.deleteChart(chartId));

  deleteChartMetric = (metricId: any) =>
    this.instance.delete(breakpoints.deleteChartMetric(metricId));

  reorderChartMetric = ({ request_body }: any) =>
    this.instance.put(breakpoints.reorderChartMetric(), request_body);

  fetch_dashboardid = (modelId: any) => this.instance.get(breakpoints.dashboard(modelId));

  create_dashboard = ({ request_body }: any) =>
    this.instance.post(breakpoints.createDashboard(), request_body);

  update_dashboard = ({ request_body }: any) =>
    this.instance.put(breakpoints.updateDashboard(), request_body);

  dashboard_time_filter = ({ request_body }: any) =>
    this.instance.post(breakpoints.dashboardTimeFilter(), request_body);

  dashboard_metadata = (dashboardId: string) =>
    this.instance.get(breakpoints.dashboardMetadata(dashboardId));

  dashboard_full_export_pdf = (
    dashboardId: string,
    startDate: string,
    endDate: string,
    scenarioId: string,
    modelId: string,
  ) =>
    this.instance.get(
      breakpoints.dashboardFullExport(dashboardId, startDate, endDate, scenarioId, modelId),
    );

  /**
   * Dashboard Data Values API with custom headers and time range
   * @param chartId - page/chart UUID
   * @param scenarioId - scenario ID
   * @param startDate - e.g. 'Sep-24'
   * @param endDate - e.g. 'Sep-25'
   * @param reportingPeriod - optional reporting period object with preset, start, end
   * @param headers - optional custom headers
   */
  getDashboardDataValuesWithTime = (
    chartId: string,
    scenarioId: string | number,
    startDate: string,
    endDate: string,
    reportingPeriod?: { preset: string; start: string; end: string },
    compareScenarioId?: string | number,
    headers: Record<string, string> = {},
  ) => {
    let url = `/page/data_values/${chartId}/${scenarioId}?start_date=${encodeURIComponent(
      startDate,
    )}&end_date=${encodeURIComponent(endDate)}`;

    if (reportingPeriod) {
      url += `&preset=${encodeURIComponent(reportingPeriod.preset || '')}`;
      // Only include start and end when preset is Custom
      if (reportingPeriod.preset === 'Custom') {
        url += `&start=${encodeURIComponent(reportingPeriod.start || '')}`;
        url += `&end=${encodeURIComponent(reportingPeriod.end || '')}`;
      }
    }

    if (compareScenarioId) {
      url += `&compare_scenario=${encodeURIComponent(compareScenarioId)}`;
    }

    return this.instance.get(url, { headers });
  };

  // Page management APIs
  create_page = ({ request_body }: any) => {
    return this.instance.post(breakpoints.createPage(), request_body);
  };

  update_page = ({ request_body }: any) => {
    return this.instance.put(breakpoints.updatePage(), request_body);
  };

  delete_page = (pageId: string) => {
    return this.instance.delete(breakpoints.deletePage(pageId));
  };
}

export default new BloxBiService();
